import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Phone, Mail, MapPin, Star } from 'lucide-react';

const Footer = () => {
  return (
    <footer className="bg-gray-900 text-gray-300" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="col-span-1 md:col-span-2">
            <div className="flex items-center space-x-2 mb-4">
              <Shield className="w-8 h-8 text-[#0066cc]" />
              <span className="text-xl font-bold text-white">Handwerkernotdienst NRW</span>
            </div>
            <p className="text-gray-400 mb-4">
              Schössow Dienstleistungen UG (haftungsbeschränkt)<br />
              Ihr zuverlässiger Partner für Notfälle rund um die Uhr im Raum Dortmund und Umgebung.
            </p>
            <div className="space-y-2">
              <div className="flex items-center space-x-2" data-testid="footer-phone">
                <Phone className="w-4 h-4" />
                <a href="tel:+4923139083687" className="hover:text-white transition-colors">
                  +49 231 39083687
                </a>
              </div>
              <div className="flex items-center space-x-2" data-testid="footer-email">
                <Mail className="w-4 h-4" />
                <a href="mailto:info@handwerkernotdienst.nrw" className="hover:text-white transition-colors">
                  info@handwerkernotdienst.nrw
                </a>
              </div>
              <div className="flex items-center space-x-2" data-testid="footer-address">
                <MapPin className="w-4 h-4" />
                <span>Neben dem Brand 10, 44135 Dortmund</span>
              </div>
              <div className="mt-4 pt-4 border-t border-gray-700">
                <a 
                  href="https://www.google.com/maps/place/Handwerkernotdienst.nrw/@51.5079827,7.3054521,27895m/data=!3m2!1e3!4b1!4m6!3m5!1s0x2c44deb7de2690b3:0x94f2797019a9355e!8m2!3d51.5078845!4d7.4702625!16s%2Fg%2F11xm_663l8?entry=ttu&g_ep=EgoyMDI1MTAyNi4wIKXMDSoASAFQAw%3D%3D" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center space-x-2 bg-[#0066cc] text-white px-4 py-2.5 rounded-lg font-semibold hover:bg-[#0052a3] transition-colors"
                  data-testid="footer-google-business"
                >
                  <Star className="w-5 h-5" />
                  <span>Bewerten Sie uns auf Google</span>
                </a>
              </div>
              
              {/* Social Media Links */}
              <div className="mt-6 pt-6 border-t border-gray-700">
                <h4 className="text-white font-semibold mb-3">Folgen Sie uns</h4>
                <div className="flex space-x-4">
                  <a
                    href="https://www.youtube.com/@Handwerkernotdienst"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-gray-800 hover:bg-[#FF0000] rounded-full flex items-center justify-center transition-colors"
                    aria-label="YouTube"
                    data-testid="social-youtube"
                  >
                    <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
                      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                    </svg>
                  </a>
                  <a
                    href="https://www.tiktok.com/@handwerkernotdienst.nrw"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-gray-800 hover:bg-black rounded-full flex items-center justify-center transition-colors"
                    aria-label="TikTok"
                    data-testid="social-tiktok"
                  >
                    <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-semibold mb-4">Schnellzugriff</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="hover:text-white transition-colors" data-testid="footer-link-home">
                  Startseite
                </Link>
              </li>
              <li>
                <Link to="/einsatzgebiete" className="hover:text-white transition-colors" data-testid="footer-link-coverage">
                  Einsatzgebiete
                </Link>
              </li>
              <li>
                <Link to="/kontakt" className="hover:text-white transition-colors" data-testid="footer-link-contact">
                  Kontakt
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="text-white font-semibold mb-4">Rechtliches</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/impressum" className="hover:text-white transition-colors" data-testid="footer-link-imprint">
                  Impressum
                </Link>
              </li>
              <li>
                <Link to="/datenschutz" className="hover:text-white transition-colors" data-testid="footer-link-privacy">
                  Datenschutz
                </Link>
              </li>
              <li>
                <Link to="/agb" className="hover:text-white transition-colors" data-testid="footer-link-terms">
                  AGB
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Google My Business Section */}
        <div className="border-t border-gray-800 mt-8 pt-8">
          <div className="text-center max-w-2xl mx-auto">
            <h3 className="text-white font-bold text-xl mb-3">Finden Sie uns auf Google</h3>
            <p className="text-gray-400 mb-4">
              Besuchen Sie unseren Google My Business Eintrag für Öffnungszeiten, Bewertungen und direkte Navigation zu uns.
            </p>
            <a 
              href="https://www.google.com/maps/place/Handwerkernotdienst.nrw/@51.5079827,7.3054521,27895m/data=!3m2!1e3!4b1!4m6!3m5!1s0x2c44deb7de2690b3:0x94f2797019a9355e!8m2!3d51.5078845!4d7.4702625!16s%2Fg%2F11xm_663l8?entry=ttu&g_ep=EgoyMDI1MTAyNi4wIKXMDSoASAFQAw%3D%3D" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-3 bg-white text-gray-900 px-6 py-3 rounded-full font-bold hover:bg-gray-100 transition-colors shadow-lg"
              data-testid="footer-google-maps-link"
            >
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
              </svg>
              <span>Auf Google Maps öffnen</span>
            </a>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-500 text-sm">
          <p>© {new Date().getFullYear()} Handwerkernotdienst NRW – Schössow Dienstleistungen UG (haftungsbeschränkt). Alle Rechte vorbehalten.</p>
          <p className="mt-2">HRB 29517, Amtsgericht Dortmund | USt.-ID: DE310018782</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;